// Funnel attribution. Mirrors `referralAttribution.js` exactly — capture on
// first paint, persist to localStorage so it survives reloads and new tabs,
// ride along in the session-create payload. Nothing here is a new mechanism.
//
// TWO DIFFERENT QUESTIONS, TWO FIELDS.
//
//   funnelSite   — WHICH of the nine landing domains the seller is on, read
//                  from `window.location.hostname`. Free, present on every
//                  pageview, needs no per-app configuration, and cannot be
//                  forgotten on one of the eight manually-zipped Amplify apps.
//                  This is the page's OWN address, not the referrer header:
//                  in-app browsers strip the referrer, they do not strip the
//                  URL you are looking at.
//
//   funnelSource — WHICH source drove the visit (a clipping agency, a creator,
//                  an ad set), read from `?fs=<tag>`. The hostname cannot
//                  answer this: several sources point at the same domain. The
//                  tag is appended by whoever hands the link out.
//
// WHAT THIS DOES NOT COVER, stated so nobody reads tagged-share as total
// coverage: localStorage is per-origin and per-device. A seller who browses on
// a phone and completes on a desktop arrives with no stored tag and is
// recorded `no_param`. Nothing better exists that is not the referrer header
// or an IP match, and both of those are why the honest answer on 2026-09-09
// was 3 sessions provable against a ceiling of 33.
//
// NEVER A BARE NULL: a session that cannot name its funnel is stored
// unattributable WITH a reason, so it stays countable. See
// `attributionValue.js` and migration 233.

import { normaliseAttributionValue, REASONS } from './attributionValue';

const STORAGE_KEY = 'takeover.funnelAttribution';

// Aliases for the funnel tag, cheapest first. `fs` is the short form we hand
// out; the long forms exist because link-builders and spreadsheets expand it.
const SOURCE_PARAMS = ['fs', 'funnel', 'funnel_source'];

// A funnel tag is a slug someone types into a link. Keep it boring so a
// mangled link is recorded as `invalid` rather than stored as a source.
const SOURCE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,63}$/;

/** Current landing domain, lower-cased, `www.` and any port stripped. */
export function readFunnelSite() {
  if (typeof window === 'undefined' || !window.location) return null;
  try {
    const host = String(window.location.hostname || '').trim().toLowerCase();
    if (!host) return null;
    return host.replace(/^www\./, '').slice(0, 253) || null;
  } catch {
    return null;
  }
}

/**
 * Read `?fs=<tag>` off the URL, persist it, and return
 * `{ source, reason, site }`.
 *
 * `reason` is ALWAYS set. A fresh tag on the URL always wins over stale
 * storage; when the URL carries no usable tag we fall back to what this origin
 * already stored, and only if that is empty too do we report why we have
 * nothing.
 *
 * Idempotent within a page load — safe to call on every render.
 */
export function captureFunnelAttribution() {
  const site = readFunnelSite();
  if (typeof window === 'undefined') {
    return { source: null, reason: REASONS.CAPTURE_FAILED, site };
  }

  let firstReason = REASONS.NO_PARAM;
  let captured = null;
  try {
    const params = new URLSearchParams(window.location.search);
    for (const name of SOURCE_PARAMS) {
      const { value, reason } = normaliseAttributionValue(params.get(name), SOURCE_PATTERN);
      if (value) { captured = value; break; }
      // Keep the FIRST informative reason: a present-but-broken `?fs=` is a
      // more useful thing to record than "the alias `funnel` was absent".
      if (firstReason === REASONS.NO_PARAM && reason !== REASONS.NO_PARAM) {
        firstReason = reason;
      }
    }
  } catch {
    return { source: null, reason: REASONS.CAPTURE_FAILED, site };
  }

  try {
    if (captured) {
      const fresh = { source: captured, reason: REASONS.CAPTURED, site };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
      return fresh;
    }
    // Read the store RAW here rather than through the exported helper: that
    // one swallows a throw and returns null, which would report a blocked
    // localStorage as `no_param` — an absence we can act on standing in for a
    // failure we cannot. Let it throw into the catch below.
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const stored = raw ? JSON.parse(raw) : null;
    if (stored && stored.source) {
      return { source: stored.source, reason: REASONS.CAPTURED, site };
    }
    return { source: null, reason: firstReason, site };
  } catch {
    // localStorage throws in private browsing / when site data is blocked.
    // Attribution is best-effort and must never block the takeover: report
    // the tag we read this paint, and say the store was unreachable when we
    // had nothing to report.
    return captured
      ? { source: captured, reason: REASONS.CAPTURED, site }
      : { source: null, reason: REASONS.CAPTURE_FAILED, site };
  }
}

/** The persisted `{ source, reason, site }`, or null if none/unreadable. */
export function readStoredFunnelAttribution() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.source) return null;
    return { source: parsed.source, reason: REASONS.CAPTURED, site: parsed.site || null };
  } catch {
    return null;
  }
}

// Ad-platform source from `?utm_source=` (migration 343). A different question
// from `?fs=`: `fs` is a tag we hand out, `utm_source` is what an ad platform
// writes into its own click URL (Snapchat ads arrive as `utm_source=snapchat`).
// Same capture rules as the funnel tag: a fresh value on the URL wins, else the
// one this origin stored, lower-cased so `Snapchat` and `snapchat` are one
// channel. No reason column: a NULL here only ever means "the visit carried none".
const UTM_STORAGE_KEY = 'takeover.utmSource';
const UTM_SOURCE_PATTERN = /^[a-z0-9][a-z0-9_.-]{0,63}$/;

/** The landing URL's `utm_source` (persisted), or the stored one, or null. */
export function captureUtmSource() {
  if (typeof window === 'undefined') return null;
  let captured = null;
  try {
    const raw = new URLSearchParams(window.location.search).get('utm_source');
    const lowered = typeof raw === 'string' ? raw.trim().toLowerCase() : raw;
    captured = normaliseAttributionValue(lowered, UTM_SOURCE_PATTERN).value;
  } catch {
    captured = null;
  }
  try {
    if (captured) {
      window.localStorage.setItem(UTM_STORAGE_KEY, captured);
      return captured;
    }
    const stored = window.localStorage.getItem(UTM_STORAGE_KEY);
    return stored && UTM_SOURCE_PATTERN.test(stored) ? stored : null;
  } catch {
    // localStorage blocked: still report what this paint read.
    return captured;
  }
}

// Which ad platform's click id the landing URL carried (migration 343). Each
// platform appends its own to every ad click: Snapchat `ScCid`, Meta `fbclid`.
// Only the platform is kept, never the id. Same durability as utm_source.
const CLICK_STORAGE_KEY = 'takeover.adClickPlatform';
const CLICK_PARAMS = [
  ['snapchat', ['ScCid', 'sccid']],
  ['meta', ['fbclid']],
];
const CLICK_PLATFORMS = new Set(CLICK_PARAMS.map(([platform]) => platform));

/** 'snapchat' | 'meta' from the landing URL's click id (persisted), or the stored one, or null. */
export function captureAdClickPlatform() {
  if (typeof window === 'undefined') return null;
  let captured = null;
  try {
    const params = new URLSearchParams(window.location.search);
    const hit = CLICK_PARAMS.find(([, names]) => names.some((name) => {
      const raw = params.get(name);
      return typeof raw === 'string' && raw.trim() !== '';
    }));
    captured = hit ? hit[0] : null;
  } catch {
    captured = null;
  }
  try {
    if (captured) {
      window.localStorage.setItem(CLICK_STORAGE_KEY, captured);
      return captured;
    }
    const stored = window.localStorage.getItem(CLICK_STORAGE_KEY);
    return CLICK_PLATFORMS.has(stored) ? stored : null;
  } catch {
    return captured;
  }
}

/**
 * The `{ funnelSource, funnelReason, funnelSite }` triple for the
 * session-create payload, plus `utmSource` and `adClickPlatform`. Always returns
 * every key — the reason is the point, so it is never conditionally omitted the
 * way `referralCode` is.
 */
export function funnelAttributionPayload() {
  const { source, reason, site } = captureFunnelAttribution();
  return {
    funnelSource: source,
    funnelReason: reason,
    funnelSite: site,
    utmSource: captureUtmSource(),
    adClickPlatform: captureAdClickPlatform(),
  };
}
